import React, { useContext, useEffect, useState, useMemo } from 'react';
import { useSelector } from 'react-redux';

import gql from 'graphql-tag';

import { useMutation, useLazyQuery } from '@apollo/client';
import { DropzoneArea } from 'mui-file-dropzone';
import makeStyles from '@mui/styles/makeStyles';

import Grid2 from '@mui/material/Grid2';
import MenuItem from '@mui/material/MenuItem';
import InputLabel from '@mui/material/InputLabel';

import FormControl from '@mui/material/FormControl';
import TextField from '@mui/material/TextField';
import { DocumentContext } from '../../contexts/DocumentContext';
import { rejectHiddenDocumentTypeOptions } from '../../services/pcwService';

import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import Autocomplete from '@mui/material/Autocomplete';
import Select from '@mui/material/Select';
import { Button, Typography, FormHelperText } from '@mui/material';

import _ from 'lodash';
import {
  blackColor,
  grayColor,
  hexToRgb,
  primaryBoxShadow,
  primaryColor,
  whiteColor,
} from 'assets/jss/material-dashboard-pro-react.js';
import { documentsByKey } from '../../constants/common';
import { Modal } from '@mui/material';
export const GET_CREDIT_SUBMISSIONS = gql`
  query CreditSubmissionsByDynamicsVOId($id: ID!, $display: String) {
    creditSubmissionsByDynamicsVOId(id: $id, display: $display) {
      creditSubmissionId
      creditSubId
      submissionDate
      decision
      lenderName
      amountRequested
      approvedAmount
      notes
      contingencies
      approvedRate
      creditSubStage
      apiApplicationId
      apiResult
      apiExpiredCredentials
      apiDetail
      lenderProfileId
      lenderChannelCode
      lenderTypeCode
      draft
      creditSubStage
      transactionSummary
      mappedValues
      source
      submittedBy
      lenderContactInfo {
        id
        email
        dynamicsContactId
        fullName
        title
        phoneNumber
        accountId
      }
      fastTrackResults {
        approvedBy
        fastTrackId
        maxApprovalAmount
        rate
        inputs {
          key
          value
        }
        outputs {
          key
          value
        }
      }
    }
  }
`;

const getModalStyle = () => {
  return {
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: '50vw',
  };
};

const UPLOAD_FILE = gql`
  mutation ($input: UpsertDocumentInput!, $file: Upload) {
    upsertDocument(input: $input, file: $file)
  }
`;

const useStyles = makeStyles(theme => ({
  button: {
    margin: theme.spacing(0, 2),
  },
  paper: {
    position: 'absolute',
    width: 600,
    backgroundColor: theme.palette.background.paper,
    border: '2px solid #000',
    boxShadow: theme.shadows[5],
    padding: theme.spacing(2, 4, 3),
  },
  container: {
    display: 'flex',
    flexDirection: 'column',
    flexWrap: 'wrap',
  },
  formControl: {
    margin: theme.spacing(1),
    minWidth: 240,
  },
  primaryBorderColor: {
    '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
      borderColor: theme.palette.primary.main,
    },
    '& .MuiInputLabel-shrink': {
      color: theme.palette.primary.main,
    },
  },
  hideDropZone: {
    display: 'none',
  },
  showDropZone: {
    display: 'block',
  },
  selectMenuItem: {
    fontSize: '13px',
    padding: '10px 20px',
    margin: '0 5px',
    borderRadius: '2px',
    transition: 'all 150ms linear',
    display: 'block',
    clear: 'both',
    fontWeight: '400',
    lineHeight: '2',
    whiteSpace: 'nowrap',
    color: grayColor[7],
    paddingRight: '30px',
    '&:hover': {
      backgroundColor: primaryColor[0],
      color: whiteColor,
      ...primaryBoxShadow,
    },
  },
  modalStyle: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: 600,
    backgroundColor: 'white',
    border: '1px solid #000',
    padding: 32,
  },
}));

export default function FileUploadModal(props) {
  const userProfile = useSelector(state => state.userProfile);

  const predefinedFileType = props?.predefinedFileType;

  const iniitalErrors = {
    fileType: false,
    targetCreditSubmission: false,
    fileDescription: false,
  };

  const [creditSubmissionsAvailableForChoosing, setCreditSubmissionsAvailableForChoosing] = useState([]);
  const [targetCreditSubmissionId, setTargetCreditSubmissionId] = useState(_.get(props, 'creditSubmissionId', ''));
  const [fileType, setFileType] = useState(predefinedFileType || '');
  const [relatedTo, setRelatedTo] = useState(
    props.relatedTo || _.get(props, 'creditSubmissionId', '') ? 'submission' : 'application'
  );

  const [availableDealerContacts, setAvailableDealerContacts] = useState([]);
  const [availableLenderContacts, setAvailableLenderContacts] = useState([]);
  const [errors, setErrors] = useState(iniitalErrors);

  // Memoize lenderContactInfo to prevent unnecessary re-renders
  const lenderContactInfo = useMemo(() => {
    return _.get(props, 'lenderContactInfo', []);
  }, [props.lenderContactInfo]);

  const [getCreditSubmissions, { data: creditSubmissionsData }] = useLazyQuery(GET_CREDIT_SUBMISSIONS);

  // Extract primitive values to prevent useEffect from running on every render
  const vo = props.vo;
  const vp = props.vp;
  const dynamicsVendorOpportunityId = vo?.dynamicsVendorOpportunityId;
  const creditSubmissionDisplay = vp?.creditSubmissionDisplay;
  const salesRepId = vo?.salesRepresentative?.id;
  const salesManagerId = vo?.salesManager?.id;
  const financeManagerId = vo?.financeManager?.id;

  // Memoize dealer contacts to prevent unnecessary re-renders
  const dealerContacts = useMemo(() => {
    const salesRep = vo?.salesRepresentative;
    const salesManager = vo?.salesManager;
    const financeManager = vo?.financeManager;
    return _.uniqBy(
      [salesRep, salesManager, financeManager].filter(x => x !== null),
      'id'
    );
  }, [salesRepId, salesManagerId, financeManagerId]);

  useEffect(() => {
    if (!dynamicsVendorOpportunityId) return;

    setAvailableDealerContacts(dealerContacts);

    const variables = {
      id: dynamicsVendorOpportunityId,
    };
    if (creditSubmissionDisplay) {
      variables.display = creditSubmissionDisplay;
    }

    getCreditSubmissions({
      variables,
    });
  }, [dynamicsVendorOpportunityId, creditSubmissionDisplay, dealerContacts, getCreditSubmissions]);

  useEffect(() => {
    // Always process credit submissions data for the dropdown
    const dataAvailable = _.get(creditSubmissionsData, 'creditSubmissionsByDynamicsVOId', []);
    if (dataAvailable && dataAvailable.length > 0) {
      const filteredData = targetCreditSubmissionId
        ? dataAvailable.filter(cs => cs?.creditSubId === targetCreditSubmissionId)
        : dataAvailable.filter(Boolean);

      setCreditSubmissionsAvailableForChoosing(filteredData);

      // Set lender contacts from props if available, otherwise extract from data
      if (lenderContactInfo && lenderContactInfo.length > 0) {
        setAvailableLenderContacts(lenderContactInfo);
      } else {
        const lenderContacts = _.flatten(filteredData.map(cs => cs.lenderContactInfo).filter(x => x !== null));
        if (lenderContacts.length > 0) {
          setAvailableLenderContacts(_.uniqBy(lenderContacts, 'id'));
        }
      }
    } else if (lenderContactInfo && lenderContactInfo.length > 0) {
      // If no credit submissions data but lenderContactInfo provided, still set it
      setAvailableLenderContacts(lenderContactInfo);
    }
  }, [creditSubmissionsData, lenderContactInfo, targetCreditSubmissionId]);

  const classes = useStyles();
  const [modalStyle] = useState(getModalStyle);
  const [openErrorDialogue, setOpenErrorDialogue] = useState(false);
  // const [openDescriptionDialogue, setOpenDescriptionDialogue] = useState(false);
  const [openUploadZone, setOpenUploadZone] = useState(false);
  const [mutationError, setMutationError] = useState('');

  const [fileDescription, setFileDescription] = useState('');
  const [openFileDescriptionInputModal, setOpenFileDescriptionInputModal] = useState(false);
  const [openInvoiceQuoteUploadModal, setOpenInvoiceQuoteUploadModal] = useState(false);
  const [sendNotifications, setSendNotifications] = useState(true);
  const [selectedDealerContact, setSelectedDealerContact] = useState([]);
  const [selectedLenderContact, setSelectedLenderContact] = useState([]);
  const [fileObjects, setFileObjects] = useState([]);
  const [fileInput] = useMutation(UPLOAD_FILE, {
    context: { authRequired: true },
  });
  const documentContext = useContext(DocumentContext);
  const graphqlErrorMessage = {
    duplicateMessage:
      'GraphQL error: duplicate key value violates unique constraint "document_vendor_opportunity_id_doc_name_key"',
  };

  const errorDialogue = (
    <div style={modalStyle} className={classes.paper}>
      <h2 id="simple-modal-title">Error</h2>
      <p id="simple-modal-description">
        {!!mutationError && mutationError.message === graphqlErrorMessage.duplicateMessage
          ? 'This file is already uploaded'
          : ''}
      </p>
    </div>
  );

  const handleOpenErrorDialogue = () => setOpenErrorDialogue(true);
  const handleCloseErrorDialogue = () => setOpenErrorDialogue(false);
  const handleOpenUploadZone = () => setOpenUploadZone(true);
  const handleCloseUploadZone = () => setOpenUploadZone(false);
  const handleOpenFileDescriptionInputModal = () => setOpenFileDescriptionInputModal(true);
  const handleCloseFileDescriptionInputModal = () => setOpenFileDescriptionInputModal(false);
  const handleOpenInvoiceQuoteUploadModal = () => setOpenInvoiceQuoteUploadModal(true);
  const handleCloseInvoiceQuoteUploadModal = () => setOpenInvoiceQuoteUploadModal(false);

  const documentTypeOptionsBase = [documentsByKey.quote, documentsByKey.invoice];

  const documentTypeOptionsFull = [
    documentsByKey.quote,
    documentsByKey.invoice,
    documentsByKey.buyers_order,
    documentsByKey.decision_letter,
    documentsByKey.credit_report,
    documentsByKey.insurance_certificate,
    documentsByKey.driver_license,
    documentsByKey.bank_statements,
    documentsByKey.tax_returns,
    documentsByKey.equipment_specs,
    documentsByKey.sales_tax_exemption,
    documentsByKey.credit_app_scan,
    documentsByKey.financial,
    documentsByKey.other,
  ];

  let documentTypeOptions = props.equipmentUpload ? documentTypeOptionsBase : documentTypeOptionsFull;
  documentTypeOptions = rejectHiddenDocumentTypeOptions(
    props.documentsPortalConfiguration,
    documentTypeOptions,
    userProfile.vendorContactRole
  );

  const handleSaveFile = async uploadFile => {
    if (uploadFile.length === 0) setMutationError('No File Selected');

    try {
      let variables = {
        input: {
          vendorOpportunityId: props.vo.vendorOpportunityId,
          source: _.get(uploadFile, '[0].name', fileDescription),
          docType: fileType,
          docDescription: fileDescription,
          createdBy: 'testing',
          docName: _.get(uploadFile, '[0].name', fileDescription),
          notifyUserProfileIds: selectedDealerContact.concat(selectedLenderContact),
        },
        file: uploadFile[0],
      };
      if (targetCreditSubmissionId) {
        variables.input = {
          ...variables.input,
          creditSubId: targetCreditSubmissionId,
        };
      }

      const response = await fileInput({
        variables,
      });

      await documentContext.setLastDocument(response.data.upsertDocument);
      if (props?.onFileUploaded) {
        props.onFileUploaded(response.data.upsertDocument);
      }
      setFileType('');
      setFileDescription('');
    } catch (err) {
      setMutationError(err);
      handleOpenErrorDialogue();
      setFileType('');
      setFileDescription('');
    }
  };

  const handleCheckboxChange = event => {
    setSendNotifications(event.target.checked);
  };

  const handleChangeDocDescription = event => {
    setFileDescription(event.target.value);
  };

  const handleFileDrop = file => {
    if (file.length > 0) {
      handleSaveFile(file);
    }
  };

  const checkIfValid = () => {
    const errors = getFieldsErrors();
    setErrors(errors);
    const hasErrors = Object.values(errors).some(item => item === true);
    return fileObjects.length > 0 && !hasErrors;
  };

  const getFieldsErrors = () => {
    return {
      fileType: !fileType,
      targetCreditSubmission: relatedTo === 'submission' && !targetCreditSubmissionId,
      fileDescription: fileType === 'other' && !fileDescription,
    };
  };

  const handleSubmit = () => {
    const isValid = checkIfValid();
    if (!isValid) {
      return;
    }
    handleFileDrop(fileObjects);
    handleCloseUploadZone();
    setSelectedDealerContact([]);
    setSelectedLenderContact([]);
    setOpenInvoiceQuoteUploadModal(false);
    props.equipmentUpload === true ? handleCloseInvoiceQuoteUploadModal() : handleCloseFileDescriptionInputModal();
    setFileType('');
    setFileDescription('');
  };

  const handleClose = () => {
    handleCloseUploadZone();
    setFileObjects([]);
    setSelectedDealerContact([]);
    setSelectedLenderContact([]);
    setOpenInvoiceQuoteUploadModal(false);
    props.equipmentUpload === true ? handleCloseInvoiceQuoteUploadModal() : handleCloseFileDescriptionInputModal();
    setFileType('');
    setFileDescription('');
  };

  return (
    <div>
      <Button color="primary" size="small" onClick={handleOpenUploadZone}>
        {props.buttonDescription ? props.buttonDescription() : ''}
      </Button>

      <Modal open={openUploadZone} onClose={handleClose}>
        <Grid2 container spacing={2} className={classes.modalStyle}>
          <Grid2 size={12}>
            <Grid2 container>
              <Grid2 size={12} mb={2}>
                <Typography variant="h5">Upload File</Typography>
              </Grid2>
              <Grid2 size={12}>
                <FormControlLabel
                  control={<Checkbox checked={sendNotifications} onChange={handleCheckboxChange} size="small" />}
                  label="Send notifications"
                />
              </Grid2>
            </Grid2>
          </Grid2>
          <Grid2 size={12}>
            {sendNotifications && (
              <Grid2 container spacing={2}>
                <Grid2 size={6}>
                  <Autocomplete
                    multiple
                    size="small"
                    onChange={(event, val) => {
                      setSelectedDealerContact(val.map(obj => obj.id));
                    }}
                    isOptionEqualToValue={(option, value) => {
                      return option.id === value;
                    }}
                    getOptionLabel={option => option.fullName || ''}
                    value={availableDealerContacts.filter(val => val && selectedDealerContact.includes(val.id))}
                    options={availableDealerContacts.filter(val => val && !selectedDealerContact.includes(val.id))}
                    renderInput={params => (
                      <TextField
                        {...params}
                        label="Dealer Contact to Notify"
                        variant="outlined"
                        formControlProps={{
                          fullWidth: true,
                        }}
                        placeholder=""
                      />
                    )}
                  />
                </Grid2>
                <Grid2 size={6}>
                  <Autocomplete
                    multiple
                    size="small"
                    getOptionLabel={option => option.fullName || ''}
                    value={availableLenderContacts.filter(val => val && selectedLenderContact.includes(val.id))}
                    options={availableLenderContacts.filter(val => val && !selectedLenderContact.includes(val.id))}
                    onChange={(event, val) => {
                      setSelectedLenderContact(val.map(obj => obj.id));
                    }}
                    renderInput={params => (
                      <TextField
                        {...params}
                        label="Lender Contact to Notify"
                        variant="outlined"
                        formControlProps={{
                          fullWidth: true,
                        }}
                        placeholder=""
                      />
                    )}
                  />
                </Grid2>
              </Grid2>
            )}
          </Grid2>
          <Grid2 size={12}>
            <Grid2 container spacing={4}>
              {openFileDescriptionInputModal ? (
                <Grid2 size={12}>
                  <form>
                    <Grid2 container spacing={5}>
                      <Grid2 size={6}>
                        <FormControl required error={errors.fileType} fullWidth style={{ margin: '8px 0' }}>
                          <InputLabel id="lender-select">Document Type</InputLabel>
                          <Select
                            color="primary"
                            variant="outlined"
                            label="Document Type"
                            value={fileType}
                            labelId="lender-select"
                            onChange={e => {
                              setFileType(e.target.value);
                            }}
                            inputProps={{ name: 'lenderSelect', id: 'lender-select' }}
                            disabled={!!predefinedFileType}
                          >
                            {documentTypeOptions.map((item, index) => (
                              <MenuItem key={index} value={item.value}>
                                {item.text}
                              </MenuItem>
                            ))}
                          </Select>
                          {errors.fileType && <FormHelperText>Please choose file type</FormHelperText>}
                        </FormControl>
                      </Grid2>
                      <Grid2 size={6}>
                        <FormControl required fullWidth style={{ margin: '8px 0' }}>
                          <InputLabel id="related-to">Related To</InputLabel>
                          <Select
                            variant="outlined"
                            color="primary"
                            label="Related To"
                            labelId="related-to"
                            value={relatedTo}
                            onChange={e => {
                              setRelatedTo(e.target.value);
                            }}
                            disabled={!!_.get(props, 'creditSubmissionId', '')}
                            inputProps={{ name: 'relatedTo', id: 'related-to' }}
                          >
                            <MenuItem key={0} value={'application'}>
                              Application
                            </MenuItem>
                            <MenuItem key={1} value={'submission'}>
                              Submission
                            </MenuItem>
                          </Select>
                        </FormControl>
                      </Grid2>
                      <Grid2 size={12}>
                        {relatedTo === 'submission' && (
                          <FormControl required error={errors.targetCreditSubmission} fullWidth>
                            <InputLabel id="demo-dialog-native">Select Submission</InputLabel>
                            <Select
                              color="primary"
                              variant="outlined"
                              label="Select Submission"
                              value={targetCreditSubmissionId}
                              onChange={e => {
                                if (e.target.value) {
                                  setTargetCreditSubmissionId(e.target.value);
                                }
                              }}
                              inputProps={{ name: 'creditSubmissionSelected', id: 'creditSubId-select' }}
                            >
                              {_.isEmpty(creditSubmissionsAvailableForChoosing) ? (
                                <MenuItem value="">No Credit Submissions Available</MenuItem>
                              ) : (
                                creditSubmissionsAvailableForChoosing.map(cs => {
                                  return (
                                    <MenuItem key={cs.creditSubId} value={cs.creditSubId}>
                                      {cs.lenderName} - Submitted By {cs.submittedBy} @ {cs.submissionDate}
                                    </MenuItem>
                                  );
                                })
                              )}
                            </Select>
                            {errors.targetCreditSubmission && (
                              <FormHelperText>Please choose credit submission</FormHelperText>
                            )}
                          </FormControl>
                        )}
                      </Grid2>
                      <Grid2 size={12}>
                        <FormControl fullWidth>
                          <TextField
                            id="outlined-multiline-static1"
                            label="Document Description"
                            multiline
                            rows={4}
                            value={fileDescription}
                            className={classes.primaryBorderColor}
                            variant="outlined"
                            onChange={handleChangeDocDescription}
                            required={fileType === 'other'}
                            error={errors.fileDescription}
                            helperText={errors.fileDescription ? 'Please describe your document' : false}
                          />
                        </FormControl>
                      </Grid2>
                    </Grid2>
                  </form>
                </Grid2>
              ) : null}
            </Grid2>
          </Grid2>

          <DropzoneArea
            onChange={newFileObjs => {
              setFileObjects(newFileObjs);
            }}
            onDelete={() => {
              setFileObjects([]);
              setFileType('');
              setFileDescription('');
              setOpenFileDescriptionInputModal(false);
            }}
            onDrop={
              props.equipmentUpload === true ? handleOpenInvoiceQuoteUploadModal : handleOpenFileDescriptionInputModal
            }
            filesLimit={1}
            dropzoneText={'Drag & drop or Choose File'}
            /* showPreviews={true} */
            maxFileSize={25000000}
          />

          <Grid2 item container justifyContent={'flex-end'} spacing={1} size={12}>
            <Button variant="outlined" size="small" onClick={handleClose}>
              Close
            </Button>
            <Button size="small" variant="contained" onClick={handleSubmit}>
              Submit
            </Button>
          </Grid2>
        </Grid2>
      </Modal>
    </div>
  );
}
